import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { tap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { ApiService } from 'src/app/shared/api.service';
import { CustomDataSource } from 'src/app/utils/custom-data-source';
import { UtilsService } from '../../shared/utils.service';
import { TaskAnalysesEditComponent } from '../task-analyses-edit/task-analyses-edit.component';
import { TaskAnalysesViewComponent } from '../task-analyses-view/task-analyses-view.component';
import {SiteModel} from "../../models/site.model";
import {SitesSelectorComponent} from "../../shared/sites-selector/sites-selector.component";
import {TaskAnalysisModel} from "../../models/task-analysis.model";

@Component({
  selector: 'app-task-analyses',
  templateUrl: './task-analyses.component.html',
  styleUrls: ['./task-analyses.component.css']
})
export class TaskAnalysesComponent implements OnInit, AfterViewInit {
  // columns to show and the data source
  displayedColumns: string[] = [
    'select',
    'id',
    'sites',
    'task_name',
    'date_created',
    // 'date_created_UTC',
    'actions'
  ];
  dataSource = new TaskAnalysisDataSource(this.app, this.api);

  // the paginator and sorter
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public utils: UtilsService,
    public app: AppService,
    private api: ApiService
  ) {}

  ngOnInit() {
    this.dataSource.getData();
  }

  /**
   * Handles the add operation by invoking the onEdit method.
   *
   * @return {void} This method does not return a value.
   */
  onAdd(): void {
    this.onEdit();
  }

  /**
   * Handles the editing process by opening a dialog for editing task analyses.
   *
   * @param {number|string} [id] - Optional identifier for the task analysis to edit.
   * @return {void} This method does not return a value.
   */
  onEdit(id?: number | string): void {
    this.utils.showComponentDialog(TaskAnalysesEditComponent,
      {
        task_analysis_id: id
      },
      {
        width: '700px'
      },
      (success: boolean): void => {
        if (!success) { return; }
        this.dataSource.getData(true);
      }
    );
  }

  onView(id: number) {
    this.utils.showComponentDialog(
      TaskAnalysesViewComponent,
      id,
      { width: '700px' },
      (success: boolean) => {
        if (!success) { return; }
        this.dataSource.getData(true);
      }
    );
  }

  ngAfterViewInit() {
    // reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.sort_by = this.sort.active;
      this.dataSource.sort_order = this.sort.direction;

      // Sort UTC by its corresponding date column.
      if ( this.sort.active == 'date_created_UTC' ) {
        this.dataSource.sort_by = 'date_created';
      }

      this.dataSource.getData();
    });

    // subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  onClearSearch() {
    this.dataSource.search = '';
    this.dataSource.getData(true);
  }

  onRemove(id: number) {
    const singular = this.utils.getLangTerm('task_analysis_documents.singular', 'Task Analysis Document');
    this.utils.showModal(
      `Remove ${singular}`,
      `Are you sure you want to remove this ${singular}?`,
      () => {
        this.api.laravelApiRequest(
          'delete',
          'task-analysis/' + id,
          {},
          {},
          (response) => {
            this.utils.showToast(`The ${singular} was removed.`);
            this.dataSource.selection.deselect(id);
            this.dataSource.getData();
          },
          (error) => {
            this.utils.showModal('Error', error.message);
          }
        );
      }
    );
  }

  onDeleteSelected() {
    const singular = this.utils.getLangTerm('task_analysis_documents.singular', 'Task Analysis Document');
    this.utils.showModal(
      `Remove ${singular}`,
      `Are you sure you want to remove the selected ${singular}?`,
      () => {
        this.api.laravelApiRequest(
          'delete',
          'task-analysis/' + this.dataSource.selection.selected.join(','),
          {},
          {},
          (response) => {
            this.utils.showToast(`The selected ${singular} was removed`);
            this.dataSource.selection.clear();
            this.dataSource.getData();
          },
          (error) => {
            this.utils.showModal('Error', error.message);
          }
        );
      }
    );
  }

  onSelectSites() {
    this.utils.showComponentDialog(
      SitesSelectorComponent,
      {
        selected: this.dataSource.site_ids,
        multiple: true
      },
      {
        width: '1200px'
      },
      (results) => {
        if ( typeof results !== 'undefined' ) {
          this.dataSource.site_ids = results;
          this.dataSource.getData(true);
        }
      }
    );
  }

  /**
   * Format the text display for associated sites.
   * Examples:
   *  - Head Office
   *  - 0 Sites
   *  - 1 Site (If name is not accessible otherwise the name of the site)
   *  - 2 Sites
   *  - Etc.
   * @param form
   */
  formatSitesListColumnText(task_analysis: TaskAnalysisModel) {
    // Check if we have one user.
    if ( task_analysis.sites_count == 1 ) {
      // Check if we have details of one user.
      if ( task_analysis.sites[0] ) {
        return task_analysis.sites[0].name;
      }
      // Respond with a generic 1 User response.
      return task_analysis.sites_count + ' ' + this.utils.getLangTerm('parent-child-sites-combined.singular', 'Site');
    }
    // Respond with the users count.
    return task_analysis.sites_count + ' ' + this.utils.getLangTerm('parent-child-sites-combined.plural', 'Sites');
  }

  /**
   * The tooltip text is only used if there is more than 1 site.
   * @param form
   */
  formatSitesListColumnTooltip(task_analysis: TaskAnalysisModel) {
    if ( task_analysis.sites_count > 1 ) {
      const site_names: string[] = task_analysis.sites.map((site: SiteModel) => {
        return site.name;
      });
      return site_names.join(`\n`);
    }
    return '';
  }
}

export class TaskAnalysisDataSource extends CustomDataSource {
  // record sorting and direction
  sort_by = 'task_name';
  sort_order = 'asc';

  site_ids: number[] = [];

  getData(resetOffset: boolean = false) {
    this.getDataFromLaravelAPI('task-analysis', resetOffset, () => {}, {
      site_ids: this.site_ids.join(',')
    });
  }
}
